/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Search, 
  FileText, 
  ChevronRight,
  ChevronDown,
  ExternalLink,
  Loader2,
  X,
  Building2,
  Package,
  ChevronsUpDown,
} from 'lucide-react';

// Services
import {
  // R10: Use index query key and function for cache coordination
  PLAYBOOK_QUERY_KEYS,
  playbookIndexQueryFn,
  searchPlaybooks,
  semanticSearchPlaybooks,
  readPlaybook,
  getPlaybookFreshness,
  // User settings
  getUserSettings,
  updatePlaybooksSettings,
} from '@/services/skillshop';
import type { UISettings } from '@shared/types/user_settings';
import type { PlaybookIndexEntry, PlaybookReadResult } from '@shared/schema/playbooks';

// Shared components
import { FreshnessIndicator } from '../shared/FreshnessIndicator';
import { TokenBadge } from '../shared/TokenBadge';
import { ScopeBadge, PlaybookScope } from '../shared/ScopeBadge';
import { DomainBadge } from '../shared/DomainBadge';

type SearchMode = 'keyword' | 'semantic';

// Helper to capitalize each word in a string
function capitalizeWords(str: string): string {
  return str
    .split(/[\s-_]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

// Color schemes for scope and domain dividers
const SCOPE_COLORS = {
  organizational: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-800',
    icon: 'text-blue-600',
    accent: 'bg-blue-100',
  },
  product: {
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    text: 'text-violet-800',
    icon: 'text-violet-600',
    accent: 'bg-violet-100',
  },
};

// Domain colors - cycle through these for different domains
const DOMAIN_COLORS = [
  { bg: 'bg-emerald-50/50', border: 'border-emerald-200', text: 'text-emerald-700' },
  { bg: 'bg-amber-50/50', border: 'border-amber-200', text: 'text-amber-700' },
  { bg: 'bg-rose-50/50', border: 'border-rose-200', text: 'text-rose-700' },
  { bg: 'bg-cyan-50/50', border: 'border-cyan-200', text: 'text-cyan-700' },
  { bg: 'bg-orange-50/50', border: 'border-orange-200', text: 'text-orange-700' },
  { bg: 'bg-teal-50/50', border: 'border-teal-200', text: 'text-teal-700' },
  { bg: 'bg-pink-50/50', border: 'border-pink-200', text: 'text-pink-700' },
  { bg: 'bg-lime-50/50', border: 'border-lime-200', text: 'text-lime-700' },
];

// Helper to group playbooks hierarchically by scope then domain
interface GroupedPlaybooks {
  scope: 'organizational' | 'product';
  domains: {
    domain: string;
    playbooks: PlaybookIndexEntry[];
    colorIndex: number;
  }[];
}

function groupPlaybooksHierarchically(playbooks: PlaybookIndexEntry[]): GroupedPlaybooks[] {
  const grouped: Record<string, Record<string, PlaybookIndexEntry[]>> = {
    organizational: {},
    product: {},
  };

  // Track seen playbook IDs to prevent duplicates
  const seenIds = new Set<string>();

  // Group by scope, then by domain
  for (const playbook of playbooks) {
    // Skip if we've already processed this playbook (prevent duplicates)
    if (seenIds.has(playbook.id)) {
      continue;
    }
    seenIds.add(playbook.id);

    // Normalize scope to lowercase and ensure it's a valid value
    const rawScope = (playbook.scope || 'product').toLowerCase();
    const scope = rawScope === 'organizational' ? 'organizational' : 'product';
    const domain = playbook.domain || 'General';
    
    if (!grouped[scope][domain]) {
      grouped[scope][domain] = [];
    }
    grouped[scope][domain].push(playbook);
  }

  // Convert to array format with color indices
  const result: GroupedPlaybooks[] = [];
  let globalDomainIndex = 0;

  for (const scope of ['organizational', 'product'] as const) {
    const scopeDomains = grouped[scope];
    if (!scopeDomains || Object.keys(scopeDomains).length === 0) continue;

    const domains = Object.entries(scopeDomains)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([domain, domainPlaybooks]) => ({
        domain,
        playbooks: domainPlaybooks.sort((a, b) => a.title.localeCompare(b.title)),
        colorIndex: globalDomainIndex++ % DOMAIN_COLORS.length,
      }));

    result.push({ scope, domains });
  }

  return result;
}

export function PlaybooksTab() {
  // Persist filter state in URL for shareability and navigation survival
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Read filters from URL with defaults
  const scopeFilter = (searchParams.get('scope') as 'all' | 'organizational' | 'product') || 'all';
  const domainFilter = searchParams.get('domain') || 'all';
  const searchMode = (searchParams.get('mode') as SearchMode) || 'keyword';
  const selectedPlaybookId = searchParams.get('id');
  
  // Local state for search query (not persisted to URL until search is executed)
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  
  // Collapse state for scopes and domains
  const [collapsedScopes, setCollapsedScopes] = useState<Set<string>>(new Set());
  const [collapsedDomains, setCollapsedDomains] = useState<Set<string>>(new Set());
  const [allCollapsed, setAllCollapsed] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [initialCollapseApplied, setInitialCollapseApplied] = useState(false);
  
  const queryClient = useQueryClient();

  // Fetch user settings to restore saved preferences
  const { data: userSettings } = useQuery({
    queryKey: ['user-settings'],
    queryFn: getUserSettings,
    staleTime: 5 * 60 * 1000,
  });

  // Initialize settings from user preferences once loaded
  useEffect(() => {
    if (userSettings && !settingsLoaded) {
      const playbooksSettings = (userSettings.uiSettings as UISettings)?.playbooksSettings;
      if (playbooksSettings) {
        // Restore scope filter if not in URL
        if (!searchParams.get('scope') && playbooksSettings.defaultScopeFilter) {
          setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (playbooksSettings.defaultScopeFilter !== 'all') {
              next.set('scope', playbooksSettings.defaultScopeFilter!);
            }
            return next;
          });
        }
        // Restore domain filter if not in URL
        if (!searchParams.get('domain') && playbooksSettings.defaultDomainFilter) {
          setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (playbooksSettings.defaultDomainFilter !== 'all') {
              next.set('domain', playbooksSettings.defaultDomainFilter!);
            }
            return next;
          });
        }
        // Restore collapse state
        if (playbooksSettings.allCollapsed) {
          setAllCollapsed(true);
          // Note: actual collapsed sets will be populated after groupedPlaybooks is available
        }
      }
      setSettingsLoaded(true);
    }
  }, [userSettings, settingsLoaded, searchParams, setSearchParams]);
  
  // Helper to update URL params while preserving others
  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === 'all' || value === '') {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      }
      return next;
    });
  }, [setSearchParams]);

  // Fetch playbooks via index endpoint - shares cache with parent component
  // This avoids redundant API calls since Playbooks.tsx also fetches the index
  const { data: playbookIndex, isLoading: isLoadingPlaybooks } = useQuery({
    queryKey: PLAYBOOK_QUERY_KEYS.index,
    queryFn: playbookIndexQueryFn,
    staleTime: 5 * 60 * 1000,
  });
  const playbooks = playbookIndex?.entries;

  // Search playbooks
  const { data: searchResults, isLoading: isSearching, refetch: doSearch } = useQuery({
    queryKey: ['playbooks-search', searchQuery, searchMode, scopeFilter, domainFilter],
    queryFn: async () => {
      if (!searchQuery.trim()) return null;
      
      const options = {
        scope: scopeFilter,
        domain: domainFilter !== 'all' ? domainFilter : undefined,
        limit: 50,
        includeTldr: true,
      };

      if (searchMode === 'semantic') {
        return semanticSearchPlaybooks(searchQuery, options);
      }
      return searchPlaybooks(searchQuery, options);
    },
    enabled: false, // Manual trigger
  });

  // React Query for playbook content - caches and handles loading states
  const { 
    data: playbookContent, 
    isLoading: isLoadingContent,
  } = useQuery({
    queryKey: ['playbook-content', selectedPlaybookId],
    queryFn: () => readPlaybook(selectedPlaybookId!, { full: true, includeMetadata: true }),
    enabled: !!selectedPlaybookId,
    staleTime: 5 * 60 * 1000, // Cache content for 5 minutes
  });

  // Get selected playbook metadata from list (for header display while loading)
  const selectedPlaybook = selectedPlaybookId
    ? playbooks?.find(p => p.id === selectedPlaybookId) ?? 
      searchResults?.results?.find(r => r.entry.id === selectedPlaybookId)?.entry ?? 
      null
    : null;

  // Select playbook by ID - React Query handles fetching, URL persists selection
  const selectPlaybook = useCallback((playbook: PlaybookIndexEntry) => {
    updateParams({ id: playbook.id });
  }, [updateParams]);

  // Close focus view - React Query keeps data cached
  const closeFocusView = useCallback(() => {
    updateParams({ id: null });
  }, [updateParams]);

  // Handle search and persist query to URL
  const handleSearch = useCallback(() => {
    if (searchQuery.trim()) {
      updateParams({ q: searchQuery.trim() });
      doSearch();
    }
  }, [searchQuery, doSearch, updateParams]);

  // R9: Clear search and remove from URL
  const clearSearch = useCallback(() => {
    setSearchQuery('');
    updateParams({ q: null });
    queryClient.removeQueries({ queryKey: ['playbooks-search'] });
  }, [queryClient, updateParams]);

  // Update scope filter and persist to user settings
  const handleScopeChange = useCallback((scope: 'all' | 'organizational' | 'product') => {
    updateParams({ scope });
    updatePlaybooksSettings({ defaultScopeFilter: scope });
  }, [updateParams]);

  // Update domain filter and persist to user settings
  const handleDomainChange = useCallback((domain: string) => {
    updateParams({ domain });
    updatePlaybooksSettings({ defaultDomainFilter: domain });
  }, [updateParams]);

  // Toggle collapse for a scope
  const toggleScope = useCallback((scope: string) => {
    setCollapsedScopes(prev => {
      const next = new Set(prev);
      if (next.has(scope)) {
        next.delete(scope);
      } else {
        next.add(scope);
      }
      return next;
    });
  }, []);

  // Toggle collapse for a domain
  const toggleDomain = useCallback((domainKey: string) => {
    setCollapsedDomains(prev => {
      const next = new Set(prev);
      if (next.has(domainKey)) {
        next.delete(domainKey);
      } else {
        next.add(domainKey);
      }
      return next;
    });
  }, []);

  // Get unique domains from playbooks
  const domains = playbooks 
    ? [...new Set(playbooks.map(p => p.domain).filter(Boolean))]
    : [];

  // Determine which playbooks to display
  const displayPlaybooks = searchResults?.results 
    ? searchResults.results.map(r => r.entry)
    : playbooks;

  // Filter playbooks based on scope and domain
  const filteredPlaybooks = displayPlaybooks?.filter(p => {
    if (scopeFilter !== 'all' && p.scope !== scopeFilter) return false;
    if (domainFilter !== 'all' && p.domain !== domainFilter) return false;
    return true;
  });

  // Group playbooks hierarchically for display
  const groupedPlaybooks = useMemo(() => {
    if (!filteredPlaybooks) return [];
    return groupPlaybooksHierarchically(filteredPlaybooks);
  }, [filteredPlaybooks]);

  // Apply allCollapsed state when groupedPlaybooks becomes available (only once on initial load)
  useEffect(() => {
    if (allCollapsed && groupedPlaybooks.length > 0 && !initialCollapseApplied) {
      // Apply collapse state to the current playbooks
      const scopeKeys = groupedPlaybooks.map(g => g.scope);
      const domainKeys = groupedPlaybooks.flatMap(g => 
        g.domains.map(d => `${g.scope}-${d.domain}`)
      );
      setCollapsedScopes(new Set(scopeKeys));
      setCollapsedDomains(new Set(domainKeys));
      setInitialCollapseApplied(true);
    }
  }, [allCollapsed, groupedPlaybooks, initialCollapseApplied]);

  // Toggle all collapse state
  const toggleAllCollapsed = useCallback(() => {
    const newCollapsedState = !allCollapsed;
    
    if (newCollapsedState) {
      // Collapse all - collect all scope and domain keys
      const scopeKeys = groupedPlaybooks.map(g => g.scope);
      const domainKeys = groupedPlaybooks.flatMap(g => 
        g.domains.map(d => `${g.scope}-${d.domain}`)
      );
      setCollapsedScopes(new Set(scopeKeys));
      setCollapsedDomains(new Set(domainKeys));
    } else {
      // Expand all
      setCollapsedScopes(new Set());
      setCollapsedDomains(new Set());
    }
    
    setAllCollapsed(newCollapsedState);
    setInitialCollapseApplied(true); // Mark as applied since user took manual action
    // Persist to user settings
    updatePlaybooksSettings({ allCollapsed: newCollapsedState });
  }, [allCollapsed, groupedPlaybooks]);

  return (
    <div className="flex h-[calc(100vh-320px)]">
      {/* Playbook List */}
      <div className={`flex flex-col ${selectedPlaybookId ? 'w-1/2 border-r' : 'w-full'}`}>
        {/* Search and Filters */}
        <div className="p-4 border-b space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search playbooks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-9"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {/* R9: Search mode persisted to URL */}
            <Select value={searchMode} onValueChange={(v) => updateParams({ mode: v === 'keyword' ? null : v })}>
              <SelectTrigger className="w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="keyword">Keyword</SelectItem>
                <SelectItem value="semantic">Semantic</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleSearch} disabled={!searchQuery.trim() || isSearching}>
              {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
            </Button>
          </div>
          
          <div className="flex items-center gap-4">
            {/* R9: Scope selector buttons persisted to URL and user settings */}
            <div className="flex items-center gap-1 p-1 bg-muted rounded-lg">
              <Button
                variant="ghost"
                size="sm"
                className={`px-3 ${scopeFilter === 'all' ? 'bg-white shadow-sm text-primary font-medium hover:bg-white' : 'hover:bg-muted-foreground/10'}`}
                onClick={() => handleScopeChange('all')}
              >
                All
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={`px-3 ${scopeFilter === 'organizational' ? 'bg-white shadow-sm text-primary font-medium hover:bg-white' : 'hover:bg-muted-foreground/10'}`}
                onClick={() => handleScopeChange('organizational')}
              >
                Organization
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={`px-3 ${scopeFilter === 'product' ? 'bg-white shadow-sm text-primary font-medium hover:bg-white' : 'hover:bg-muted-foreground/10'}`}
                onClick={() => handleScopeChange('product')}
              >
                Product
              </Button>
            </div>
            <Select value={domainFilter} onValueChange={handleDomainChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Domain" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Domains</SelectItem>
                {domains.map(domain => (
                  <SelectItem key={domain} value={domain}>{capitalizeWords(domain)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Collapse/Expand All Toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={toggleAllCollapsed}
              className="ml-auto flex items-center gap-1.5"
            >
              <ChevronsUpDown className="h-4 w-4" />
              {allCollapsed ? 'Expand All' : 'Collapse All'}
            </Button>
          </div>
        </div>

        {/* Playbook Cards - Hierarchical View */}
        <div className="flex-1 overflow-y-auto">
          {isLoadingPlaybooks ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredPlaybooks?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <FileText className="h-12 w-12 mb-4 opacity-50" />
              <p>No playbooks found</p>
              {searchQuery && (
                <Button variant="link" onClick={clearSearch}>Clear search</Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {groupedPlaybooks.map((scopeGroup) => {
                const isScopeCollapsed = collapsedScopes.has(scopeGroup.scope);
                
                return (
                  <div key={scopeGroup.scope}>
                    {/* Scope Divider */}
                    <ScopeDivider 
                      scope={scopeGroup.scope}
                      isCollapsed={isScopeCollapsed}
                      onToggle={() => toggleScope(scopeGroup.scope)}
                      totalPlaybooks={scopeGroup.domains.reduce((acc, d) => acc + d.playbooks.length, 0)}
                    />
                    
                    {/* Domains within scope - hidden when scope is collapsed */}
                    {!isScopeCollapsed && scopeGroup.domains.map((domainGroup) => {
                      const domainKey = `${scopeGroup.scope}-${domainGroup.domain}`;
                      const isDomainCollapsed = collapsedDomains.has(domainKey);
                      
                      return (
                        <div key={domainKey}>
                          {/* Domain Divider */}
                          <DomainDivider 
                            domain={domainGroup.domain} 
                            count={domainGroup.playbooks.length}
                            colorIndex={domainGroup.colorIndex}
                            isCollapsed={isDomainCollapsed}
                            onToggle={() => toggleDomain(domainKey)}
                          />
                          
                          {/* Playbook Cards in this domain - hidden when domain is collapsed */}
                          {!isDomainCollapsed && (
                            <div className="px-4 pb-3 space-y-2">
                              {domainGroup.playbooks.map((playbook) => (
                                <PlaybookCard
                                  key={playbook.id}
                                  playbook={playbook}
                                  isSelected={selectedPlaybookId === playbook.id}
                                  onClick={() => selectPlaybook(playbook)}
                                  compact
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Focus View (Playbook Detail) - R6: React Query handles loading/caching */}
      {selectedPlaybookId && selectedPlaybook && (
        <PlaybookFocusView
          playbook={selectedPlaybook}
          content={playbookContent ?? null}
          isLoadingContent={isLoadingContent}
          onClose={closeFocusView}
        />
      )}
    </div>
  );
}

// Scope Divider Component - Top level organizational/product separator
interface ScopeDividerProps {
  scope: 'organizational' | 'product';
  isCollapsed: boolean;
  onToggle: () => void;
  totalPlaybooks: number;
}

function ScopeDivider({ scope, isCollapsed, onToggle, totalPlaybooks }: ScopeDividerProps) {
  const colors = SCOPE_COLORS[scope];
  const Icon = scope === 'organizational' ? Building2 : Package;
  const label = scope === 'organizational' ? 'Organization' : 'Product';

  return (
    <div 
      className={`sticky top-0 z-10 ${colors.bg} ${colors.border} border-b-2 px-4 py-3 cursor-pointer hover:opacity-90 transition-opacity`}
      onClick={onToggle}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded ${colors.accent}`}>
            <Icon className={`h-4 w-4 ${colors.icon}`} />
          </div>
          <span className={`font-semibold text-sm uppercase tracking-wide ${colors.text}`}>
            {label} Playbooks
          </span>
          <span className={`text-xs ${colors.text} opacity-60`}>
            ({totalPlaybooks})
          </span>
        </div>
        <div className={`p-1 rounded hover:${colors.accent} transition-colors`}>
          {isCollapsed ? (
            <ChevronRight className={`h-4 w-4 ${colors.icon}`} />
          ) : (
            <ChevronDown className={`h-4 w-4 ${colors.icon}`} />
          )}
        </div>
      </div>
    </div>
  );
}

// Domain Divider Component - Second level domain separator within a scope
interface DomainDividerProps {
  domain: string;
  count: number;
  colorIndex: number;
  isCollapsed: boolean;
  onToggle: () => void;
}

function DomainDivider({ domain, count, colorIndex, isCollapsed, onToggle }: DomainDividerProps) {
  const colors = DOMAIN_COLORS[colorIndex];

  return (
    <div 
      className={`${colors.bg} border-l-4 ${colors.border} px-4 py-2 flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity`}
      onClick={onToggle}
    >
      <div className="flex items-center gap-2">
        {isCollapsed ? (
          <ChevronRight className={`h-3.5 w-3.5 ${colors.text}`} />
        ) : (
          <ChevronDown className={`h-3.5 w-3.5 ${colors.text}`} />
        )}
        <span className={`font-medium text-sm ${colors.text}`}>
          {capitalizeWords(domain)}
        </span>
      </div>
      <span className={`text-xs ${colors.text} opacity-70`}>
        {count} {count === 1 ? 'playbook' : 'playbooks'}
      </span>
    </div>
  );
}

// Playbook Card Component
interface PlaybookCardProps {
  playbook: PlaybookIndexEntry;
  isSelected: boolean;
  onClick: () => void;
  compact?: boolean;
}

function PlaybookCard({ playbook, isSelected, onClick, compact = false }: PlaybookCardProps) {
  // R8: Use server-provided freshness status instead of calculating client-side
  if (compact) {
    // Compact view for hierarchical display - no scope/domain badges needed
    return (
      <div
        className={`p-3 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
          isSelected ? 'border-primary bg-primary/5' : 'bg-white hover:border-gray-300'
        }`}
        onClick={onClick}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <h3 className="font-medium text-sm truncate">{playbook.title}</h3>
            </div>
            {playbook.tldr && (
              <p className="text-xs text-muted-foreground line-clamp-2 ml-6 mb-2">
                {playbook.tldr}
              </p>
            )}
            <div className="flex items-center gap-2 flex-wrap ml-6">
              {playbook.tokens && <TokenBadge tokens={playbook.tokens} />}
              <FreshnessIndicator 
                status={playbook.freshness} 
                lastUpdated={playbook.lastUpdated}
              />
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 ml-2" />
        </div>
      </div>
    );
  }

  // Full view - original design with all badges
  return (
    <div
      className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
        isSelected ? 'border-primary bg-primary/5' : 'bg-slate-50 hover:border-gray-300'
      }`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <h3 className="font-medium truncate">{playbook.title}</h3>
          </div>
          {playbook.tldr && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
              {playbook.tldr}
            </p>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <ScopeBadge scope={playbook.scope as PlaybookScope} />
            {playbook.domain && <DomainBadge domain={playbook.domain} />}
            {playbook.tokens && <TokenBadge tokens={playbook.tokens} />}
            <FreshnessIndicator 
              status={playbook.freshness} 
              lastUpdated={playbook.lastUpdated}
            />
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0 ml-2" />
      </div>
    </div>
  );
}

// Playbook Focus View Component
interface PlaybookFocusViewProps {
  playbook: PlaybookIndexEntry;
  content: PlaybookReadResult | null;
  isLoadingContent: boolean;
  onClose: () => void;
}

function PlaybookFocusView({ playbook, content, isLoadingContent, onClose }: PlaybookFocusViewProps) {
  return (
    <div className="w-1/2 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold truncate">{playbook.title}</h2>
          <p className="text-sm text-muted-foreground truncate">{playbook.path}</p>
        </div>
        <div className="flex items-center gap-2 ml-4">
          {playbook.path && (
            <Button variant="ghost" size="sm" asChild>
              <a href={`vscode://file${playbook.path}`} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-1" />
                Open
              </a>
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoadingContent ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : content ? (
          <div className="prose prose-sm max-w-none">
            {/* TL;DR Section */}
            {playbook.tldr && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="text-sm font-semibold text-blue-800 mb-2">TL;DR</h4>
                <p className="text-sm text-blue-700">{playbook.tldr}</p>
              </div>
            )}

            {/* Sections */}
            {content.sections && content.sections.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Sections</h4>
                <ul className="text-sm space-y-1">
                  {content.sections.map((section, idx) => (
                    <li key={idx} className="text-muted-foreground">{section.name}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Full Content */}
            {content.content && (
              <div className="mt-4 border-t pt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Content</h4>
                <div className="bg-gray-50 rounded-lg p-4 text-sm font-mono whitespace-pre-wrap overflow-x-auto">
                  {content.content}
                </div>
              </div>
            )}

            {/* Metadata */}
            {content.metadata && (
              <div className="mt-4 border-t pt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Metadata</h4>
                <dl className="text-sm space-y-2">
                  {content.metadata.owner && (
                    <div className="flex gap-2">
                      <dt className="text-muted-foreground w-24">Owner:</dt>
                      <dd>{content.metadata.owner}</dd>
                    </div>
                  )}
                  {content.metadata.status && (
                    <div className="flex gap-2">
                      <dt className="text-muted-foreground w-24">Status:</dt>
                      <dd>{content.metadata.status}</dd>
                    </div>
                  )}
                  {content.metadata.tags && content.metadata.tags.length > 0 && (
                    <div className="flex gap-2">
                      <dt className="text-muted-foreground w-24">Tags:</dt>
                      <dd className="flex gap-1 flex-wrap">
                        {content.metadata.tags.map((tag, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-gray-100 rounded text-xs">
                            {tag}
                          </span>
                        ))}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
